import React from 'react'
import PropTypes from 'prop-types'
import { FormattedMessage, useIntl } from 'react-intl'
import { DATA_KEYS, TONE } from '../../data/constants'
import { oneDriveSharingRows, sharePointExposureRows, siteLifecycleRows } from '../../data/mockBackedAdapters'
import { MOCK_BACKED_AREAS, buildMockGap, getDataGap } from '../../data/reportModel'
import { formatRelativeDate } from '../../data/reportCopy'
import { buildSharePointTenantSettingRows } from '../../data/sharePointTenantSettings'
import Chip from '../../components/Chip/Chip'
import InfoTooltip from '../../components/InfoTooltip/InfoTooltip'
import MockedPanel from '../../components/MockedPanel/MockedPanel'
import ReportTable from '../../components/ReportTable/ReportTable'
import { useAIRiskReportExportMode } from '../../components/RenderMode/AIRiskReportRenderModeProvider'
import SubArea from '../../components/SubArea/SubArea'
import SubBlock from '../../components/SubBlock/SubBlock'
import UnavailableCell from '../../components/UnavailableCell/UnavailableCell'
import styles from './DataExposureSection.module.scss'

const STALE_WARN_DAYS = 30
const STALE_DANGER_DAYS = 90
const MILLISECONDS_PER_DAY = 24 * 60 * 60 * 1000

const getStartOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate())

const getStalenessTone = (value) => {
  if (!value) return null

  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return null

  const daysAgo = Math.max(0, Math.floor((getStartOfDay(new Date()) - getStartOfDay(date)) / MILLISECONDS_PER_DAY))
  if (daysAgo > STALE_DANGER_DAYS) return TONE.DANGER
  if (daysAgo > STALE_WARN_DAYS) return TONE.WARN
  return null
}

const renderStaleDate = (intl, value) => {
  const label = formatRelativeDate(intl, value)
  const tone = getStalenessTone(value)
  if (!tone) return label
  return <span className={`${tone === TONE.DANGER ? 'text-danger' : 'text-warning'} fw-bold`}>{label}</span>
}

const renderCurrent = (row) => {
  if (row.licenseRequired) return <Chip tone={TONE.INFO}>{row.current}</Chip>
  if (!row.current && row.requiresDlpPolicies) {
    return (
      <Chip tone={TONE.WARN}>
        <FormattedMessage id="AI_RISK_REPORT.SHAREPOINT.MARK_NEW_FILES_SENSITIVE_BY_DEFAULT.REQUIRES_DLP" />
      </Chip>
    )
  }
  if (!row.current || row.tone === TONE.NEUTRAL) return <UnavailableCell />

  let value = row.current
  if (row.tone === TONE.DANGER) value = <strong className="text-danger fw-bold">{row.current}</strong>
  if (row.tone === TONE.WARN) value = <strong className="text-warning fw-bold">{row.current}</strong>

  if (!row.requiresDlpPolicies) return value

  return (
    <span className="d-inline-flex align-items-center gap-2">
      {value}
      <Chip tone={TONE.WARN}>
        <FormattedMessage id="AI_RISK_REPORT.SHAREPOINT.MARK_NEW_FILES_SENSITIVE_BY_DEFAULT.REQUIRES_DLP" />
      </Chip>
    </span>
  )
}

const renderSettingName = (row) => {
  const label = row.labelId ? <FormattedMessage id={row.labelId} /> : <code>{row.setting}</code>
  if (!row.tooltipId) return label
  return (
    <InfoTooltip id={`ai-risk-setting-tip-${row.key}`} messageId={row.tooltipId}>
      {label}
    </InfoTooltip>
  )
}

export default function SharePoint({ tenantSettings, dlpPolicies, score, dataAvailability }) {
  const intl = useIntl()
  const isExportMode = useAIRiskReportExportMode()
  const hasDlpPolicies = !getDataGap(dataAvailability, [DATA_KEYS.DLP_POLICIES]) && dlpPolicies.length > 0
  const settingsRows = buildSharePointTenantSettingRows(tenantSettings, intl, { hasDlpPolicies })

  const stats = [
    {
      key: 'overshared',
      value: '-',
      label: <FormattedMessage id="AI_RISK_REPORT.DATA.SHAREPOINT.STAT_OVERSHARED" />,
    },
    {
      key: 'anonymous',
      value: '-',
      label: <FormattedMessage id="AI_RISK_REPORT.DATA.SHAREPOINT.STAT_ANONYMOUS" />,
    },
    {
      key: 'filesReachable',
      value: '-',
      label: <FormattedMessage id="AI_RISK_REPORT.DATA.SHAREPOINT.STAT_FILES_REACHABLE" />,
    },
  ]

  const settingsColumns = [
    {
      key: 'setting',
      header: <FormattedMessage id="AI_RISK_REPORT.SETTINGS.COL_SETTING" />,
      render: renderSettingName,
      sortable: true,
      sortValue: (row) => row.setting,
    },
    {
      key: 'current',
      header: <FormattedMessage id="AI_RISK_REPORT.SETTINGS.COL_CURRENT" />,
      render: renderCurrent,
      sortable: true,
      sortValue: (row) => row.current,
    },
    {
      key: 'recommended',
      header: <FormattedMessage id="AI_RISK_REPORT.SETTINGS.COL_RECOMMENDED" />,
      render: (row) => row.recommended,
      sortable: true,
      sortValue: (row) => row.recommended,
    },
  ]

  const exposureColumns = [
    {
      key: 'site',
      header: <FormattedMessage id="AI_RISK_REPORT.COL.SITE" />,
      render: (row) => (
        <>
          <div className="text-body fw-bold">{row.name}</div>
          <div className={styles.cellSub}>{row.path}</div>
        </>
      ),
      sortable: true,
      sortValue: (row) => (row.name || '').toLowerCase(),
    },
    {
      key: 'files',
      header: <FormattedMessage id="AI_RISK_REPORT.COL.FILES" />,
      render: (row) => row.files,
      sortable: true,
      sortValue: (row) => row.files ?? 0,
    },
    {
      key: 'lastActivity',
      header: <FormattedMessage id="AI_RISK_REPORT.SKELETON.COL_LAST_ACTIVITY" />,
      render: (row) => renderStaleDate(intl, row.lastActivity),
      sortable: true,
      sortValue: (row) => row.lastActivity || '',
    },
    {
      key: 'sharing',
      header: <FormattedMessage id="AI_RISK_REPORT.COL.SHARING" />,
      render: (row) => <Chip tone={row.tone}>{row.sharing}</Chip>,
      sortable: true,
      sortValue: (row) => row.sharing,
    },
  ]

  const oneDriveColumns = [
    {
      key: 'user',
      header: <FormattedMessage id="AI_RISK_REPORT.COL.USER" />,
      render: (row) => (
        <>
          <div className="text-body fw-bold">{row.name}</div>
          <div className={styles.cellSub}>{row.sub}</div>
        </>
      ),
      sortable: true,
      sortValue: (row) => (row.name || '').toLowerCase(),
    },
    {
      key: 'anonFiles',
      header: <FormattedMessage id="AI_RISK_REPORT.SKELETON.COL_ANON_FILES" />,
      render: (row) => (
        <strong className={row.anonTone === TONE.WARN ? 'text-warning fw-bold' : undefined}>{row.anonFiles}</strong>
      ),
      sortable: true,
      sortValue: (row) => row.anonFiles ?? 0,
    },
    {
      key: 'externalShares',
      header: <FormattedMessage id="AI_RISK_REPORT.SKELETON.COL_EXTERNAL_SHARES" />,
      render: (row) => row.externalShares,
      sortable: true,
      sortValue: (row) => row.externalShares || '',
    },
    {
      key: 'lastShare',
      header: <FormattedMessage id="AI_RISK_REPORT.SKELETON.COL_LAST_SHARE" />,
      render: (row) => renderStaleDate(intl, row.lastShare),
      sortable: true,
      sortValue: (row) => row.lastShare || '',
    },
  ]

  const lifecycleColumns = [
    {
      key: 'site',
      header: <FormattedMessage id="AI_RISK_REPORT.COL.SITE" />,
      render: (row) => (
        <>
          <div className="text-body fw-bold">{row.name}</div>
          <div className={styles.cellSub}>{row.path}</div>
        </>
      ),
      sortable: true,
      sortValue: (row) => (row.name || '').toLowerCase(),
    },
    {
      key: 'lastActivity',
      header: <FormattedMessage id="AI_RISK_REPORT.SKELETON.COL_LAST_ACTIVITY" />,
      render: (row) => renderStaleDate(intl, row.lastActivity),
      sortable: true,
      sortValue: (row) => row.lastActivity || '',
    },
    {
      key: 'items',
      header: <FormattedMessage id="AI_RISK_REPORT.SKELETON.COL_ITEMS" />,
      render: (row) => row.items,
      sortable: true,
      sortValue: (row) => row.items ?? 0,
    },
    {
      key: 'signal',
      header: <FormattedMessage id="AI_RISK_REPORT.SKELETON.COL_SIGNAL" />,
      render: (row) => <Chip tone={row.signalTone}>{row.signal}</Chip>,
      sortable: true,
      sortValue: (row) => row.signal || '',
    },
  ]

  return (
    <SubBlock
      titleId="AI_RISK_REPORT.DATA.SHAREPOINT.TITLE"
      subtitleId="AI_RISK_REPORT.DATA.SHAREPOINT.SUBTITLE"
      score={score.value}
      tone={score.tone}
      stats={isExportMode ? null : stats}
    >
      <SubArea titleId="AI_RISK_REPORT.DATA.SHAREPOINT.SETTINGS_TITLE">
        <ReportTable columns={settingsColumns} rows={settingsRows} />
      </SubArea>
      <SubArea titleId="AI_RISK_REPORT.DATA.SHAREPOINT.OVERSHARED_TITLE" hideFromExport>
        <MockedPanel gap={buildMockGap(dataAvailability, MOCK_BACKED_AREAS.SHAREPOINT_SITES)}>
          <ReportTable columns={exposureColumns} rows={sharePointExposureRows} />
        </MockedPanel>
      </SubArea>
      <SubArea titleId="AI_RISK_REPORT.DATA.SHAREPOINT.ONEDRIVE_TITLE" hideFromExport>
        <MockedPanel gap={buildMockGap(dataAvailability, MOCK_BACKED_AREAS.SHAREPOINT_SITES)}>
          <ReportTable columns={oneDriveColumns} rows={oneDriveSharingRows} />
        </MockedPanel>
      </SubArea>
      <SubArea titleId="AI_RISK_REPORT.DATA.SHAREPOINT.LIFECYCLE_TITLE" hideFromExport>
        <MockedPanel gap={buildMockGap(dataAvailability, MOCK_BACKED_AREAS.SHAREPOINT_SITES)}>
          <ReportTable columns={lifecycleColumns} rows={siteLifecycleRows} />
        </MockedPanel>
      </SubArea>
    </SubBlock>
  )
}

SharePoint.propTypes = {
  tenantSettings: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  dlpPolicies: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  score: PropTypes.shape({ value: PropTypes.number, tone: PropTypes.string }).isRequired,
  dataAvailability: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
}
